import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useReveal } from "@/hooks/useReveal";
import { Clock, MapPin, User } from "lucide-react";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

export function Schedule({ onRegister }) {
  const head = useReveal();
  const [courses, setCourses] = useState([]);
  const [style, setStyle] = useState("Tous");
  const [day, setDay] = useState("Tous");

  useEffect(() => {
    api
      .get("/courses")
      .then(({ data }) => {
        if (Array.isArray(data)) {
          setCourses(data);
        } else {
          console.error("Invalid /courses response:", data);
          setCourses([]);
        }
      })
      .catch((error) => {
        console.error("Unable to load courses:", error);
        setCourses([]);
      });
  }, []);

  const styles = useMemo(
    () => ["Tous", ...Array.from(new Set(courses.map((c) => c.style)))],
    [courses],
  );

  const filtered = courses.filter(
    (c) =>
      (style === "Tous" || c.style === style) &&
      (day === "Tous" || c.day === day),
  );

  return (
    <section
      id="planning"
      data-testid="schedule-section"
      className="relative py-20 md:py-28 bg-ink-2"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div ref={head.ref} className={head.className}>
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-emerald-bright mb-3">
            Le planning
          </p>
          <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight max-w-3xl">
            Trouve <span className="text-mustard">ton cours</span>
          </h2>
          <p className="mt-5 max-w-2xl text-cream/70 leading-relaxed">
            Des cours 6 jours sur 7, pour tous les âges et tous les niveaux.
            Filtre et positionne-toi.
          </p>
        </div>

        {/* filters */}
        <div
          className="mt-10 flex flex-wrap gap-2"
          data-testid="schedule-filters"
        >
          {styles.map((s) => (
            <button
              key={s}
              data-testid={`filter-style-${s}`}
              onClick={() => setStyle(s)}
              className={`rounded-full px-4 py-1.5 text-sm font-sub transition ${
                style === s
                  ? "bg-mustard text-ink font-semibold"
                  : "bg-ink text-cream/70 border border-white/10 hover:border-mustard/50"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {["Tous", ...DAYS].map((d) => (
            <button
              key={d}
              data-testid={`filter-day-${d}`}
              onClick={() => setDay(d)}
              className={`rounded-full px-3 py-1 text-xs font-mono2 transition ${
                day === d
                  ? "bg-emerald text-cream"
                  : "bg-ink text-cream/50 border border-white/10 hover:border-emerald/50"
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        {/* list */}
        <div
          className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="schedule-list"
        >
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`course-${c.slug}`}
              className="rounded-2xl bg-ink border border-white/5 p-5 card-hover flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="tag-emerald rounded-md px-2 py-0.5 text-[10px] font-mono2 uppercase">
                    {c.style}
                  </span>
                  <span className="font-mono2 text-[11px] text-cream/40">
                    {c.level} · {c.age}
                  </span>
                </div>
                <h3 className="mt-2 font-sub font-bold text-lg text-cream truncate">
                  {c.title}
                </h3>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-cream/55 font-mono2">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {c.day} · {c.start}-{c.end}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {c.teacher}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {c.room}
                  </span>
                </div>
              </div>
              <button
                data-testid={`course-register-${c.slug}`}
                onClick={onRegister}
                className="shrink-0 rounded-full px-4 py-2 text-xs font-semibold border border-mustard/40 text-mustard hover:bg-mustard hover:text-ink transition"
              >
                Se positionner
              </button>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-cream/50 col-span-full py-8 text-center font-mono2 text-sm">
              Aucun cours pour ce filtre.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
